import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type Props = {
  accountId: string;
  username: string;
  role: 'SUPER_MASTER' | 'MASTER' | 'DISTRIBUTOR' | 'USER';
  canLoginAs: boolean;
  onLoginAs: (accountId: string) => Promise<void>;
  openSignal?: number;
};

const PencilIcon = () => <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true"><path fill="currentColor" d="M4 17.25V20h2.75L17.81 8.94l-2.75-2.75L4 17.25Zm15.71-10.04a1 1 0 0 0 0-1.42l-1.5-1.5a1 1 0 0 0-1.42 0l-1.17 1.17 2.75 2.75 1.34-1Z" /></svg>;
const WalletIcon = () => <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true"><path fill="currentColor" d="M3 6.5A2.5 2.5 0 0 1 5.5 4H18a2 2 0 0 1 2 2v1h.5A1.5 1.5 0 0 1 22 8.5v7A1.5 1.5 0 0 1 20.5 17H20v1a2 2 0 0 1-2 2H5.5A2.5 2.5 0 0 1 3 17.5v-11ZM20 10h-4a2 2 0 1 0 0 4h4v-4Z" /></svg>;
const LoginIcon = () => <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true"><path fill="currentColor" d="M10 4H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h5v-2H5V6h5V4Zm4.59 3.59L16 9l2 2h-8v2h8l-2 2-1.41 1.41L19 12l-4.41-4.41Z" /></svg>;

export default function AccountRowActions({ accountId, username, role, canLoginAs, onLoginAs, openSignal = 0 }: Props) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (openSignal > 0) setOpen(true);
  }, [openSignal]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const loginAs = async () => {
    if (!canLoginAs || busy) return;
    setBusy(true);
    try { await onLoginAs(accountId); }
    finally { setBusy(false); }
  };

  const editAccount = () => {
    setOpen(false);
    navigate(`/accounts/${encodeURIComponent(accountId)}/edit/`);
  };

  const paymentAccount = () => {
    setOpen(false);
    navigate(`/accounts/${encodeURIComponent(accountId)}/payment/`);
  };

  return (
    <>
      <style>{'@keyframes actionDotBreathe{0%,100%{transform:translateY(0) scale(1);opacity:.90}50%{transform:translateY(-.5px) scale(1.03);opacity:1}}'}</style>
      <button type="button" onClick={() => setOpen(true)} className={`account-role-action account-role-${role.toLowerCase().replaceAll('_', '-')} group flex h-6 w-8 shrink-0 items-center justify-center gap-[2px] rounded-lg animate-[actionDotBreathe_5.2s_ease-in-out_infinite]`} aria-label={`Open actions for ${username}`}><span className="admin-action-dot h-[3px] w-[3px] rounded-full" /><span className="admin-action-dot h-[3px] w-[3px] rounded-full" /><span className="admin-action-dot h-[3px] w-[3px] rounded-full" /></button>

      {open ? (
        <div className="fixed inset-0 z-[100] grid place-items-center p-4" role="dialog" aria-modal="true" aria-label={`Actions for ${username}`}>
          <style>{'@keyframes accountActionIn{from{opacity:0;transform:scale(.92) translateY(8px)}to{opacity:1;transform:scale(1) translateY(0)}}'}</style>
          <button type="button" className="absolute inset-0 bg-slate-950/55 backdrop-blur-sm" onClick={() => setOpen(false)} aria-label="Close actions" />
          <div className="relative w-full max-w-[19rem] animate-[accountActionIn_160ms_ease-out] overflow-hidden rounded-3xl border border-white/15 bg-[rgba(18,26,43,0.94)] p-3 text-white shadow-[0_24px_70px_rgba(0,0,0,0.5)] backdrop-blur-md">
            <div className="mb-3 flex items-center justify-between px-1">
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Account Actions</p>
                <p className="truncate text-sm font-black text-white">{username}</p>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="crystal-btn crystal-action-smoky grid h-8 w-8 place-items-center rounded-full text-lg font-bold text-slate-300">×</button>
            </div>

            <div className="grid gap-2">
              <button type="button" onClick={editAccount} className="crystal-btn crystal-sapphire flex items-center gap-3 rounded-2xl px-4 py-3 text-left font-bold text-[#DCEAF2]">
                <span className="grid h-8 w-8 place-items-center rounded-xl bg-blue-400/20"><PencilIcon /></span>
                <span><span className="block text-sm">Edit</span><span className="block text-[10px] font-medium text-blue-200/70">Account settings</span></span>
              </button>
              <button type="button" onClick={paymentAccount} className="crystal-btn crystal-smoky flex items-center gap-3 rounded-2xl px-4 py-3 text-left font-bold text-amber-200">
                <span className="grid h-8 w-8 place-items-center rounded-xl bg-amber-400/15"><WalletIcon /></span>
                <span><span className="block text-sm">Payment</span><span className="block text-[10px] font-medium text-amber-200/70">Cash / Credit</span></span>
              </button>
              <button type="button" disabled={!canLoginAs || busy} onClick={() => void loginAs()} className="crystal-btn crystal-emerald flex items-center gap-3 rounded-2xl px-4 py-3 text-left font-bold text-[#DCEAE5] disabled:opacity-40">
                <span className="grid h-8 w-8 place-items-center rounded-xl bg-white/15"><LoginIcon /></span>
                <span className="text-sm">{busy ? 'Opening…' : 'Login'}</span>
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
